var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { MessageEnum } from '../models/message.enum';
import { EngineState } from 'src/app/engine/state/engine.state';
import { ToggleVisibleNodes, SetCheckedNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { LoadStory } from 'src/app/story/state/story.actions';
import { StoryState } from 'src/app/story/state/story.state';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { Observable } from 'rxjs';
import { SetCamera, ZoomCameraStatic } from 'src/app/engine/state/camera/engine.camera.actions';
import { SetLabels } from 'src/app/story/state/labels/story.labels.actions';
import { first } from 'rxjs/operators';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { SetChapterBackground, SelectChapterById } from 'src/app/story/state/chapters/story.chapters.actions';
import { SelectedObjectUtils } from 'src/app/shared/selected-object.utils';
import { ViewActions } from 'src/app/view/state/view.actions';
import { FlyToObject, SetActiveTool, SetXrayMode, SetXrayModeCacheColorRecipes } from 'src/app/engine/state/engine.actions';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { ViewState } from 'src/app/view/state/view.state';
import { _3DUtils } from 'src/app/shared/libraries/3d-utils/3d-utils';
import { appDefaultBackground } from 'src/app/dialogs/components/background-color-dialog/background-config';
import { SceneAPIUtils } from './scene-utils';
import { disableXrayMode } from 'src/app/shared/xray-mode';
function formatPickResult(hit) {
    if (!hit) {
        return null;
    }
    return {
        objectId: hit.objectId,
        canvasPosition: {
            x: hit.canvasPos[0],
            y: hit.canvasPos[1]
        },
        mode: 'query',
        position: hit.worldPos
            ? {
                x: hit.worldPos[0],
                y: hit.worldPos[1],
                z: hit.worldPos[2]
            }
            : undefined
    };
}
function buildSceneInfoResponse(storyDisplayName, objectIds, store, graphService) {
    var selectedNodes = SelectedObjectUtils.selectedNodesAsDict(store);
    var viewGraph = store.selectSnapshot(EngineState.getViewGraph);
    var humanObjectInfo = function (objectId) {
        var humanObject = Human.scene.objects[objectId];
        // if the object isn't a leaf, don't include a selected property for it!
        var selected = humanObject.isLeaf()
            ? SelectedObjectUtils.isObjectSelected(objectId, selectedNodes, viewGraph, graphService)
            : undefined;
        // FIXME: Heads up for future readers:
        // The postmessage layer (Human API) is filtering properties that are undefined.
        // So even though the key is present in this function's return val,
        // it's being stripped before the client gets it.
        // We will not stand for censorship! Free Hong Kong!
        return {
            objectId: humanObject.objectId,
            name: humanObject.displayName,
            description: humanObject.description || '',
            shown: humanObject.shown,
            selected: selected,
            children: humanObject.objects.map(function (object) { return object.objectId; }),
            parent: humanObject.parent ? humanObject.parent.objectId : null
        };
    };
    var objects = objectIds.reduce(function (acc, objectId) {
        acc[objectId] = humanObjectInfo(objectId);
        return acc;
    }, {});
    return {
        name: storyDisplayName,
        objects: objects
    };
}
function captureObjects(store, graphService) {
    var selectedNodes = SelectedObjectUtils.selectedNodesAsDict(store);
    var viewGraph = store.selectSnapshot(EngineState.getViewGraph);
    var objectIds = Object.keys(Human.scene.objects);
    return objectIds.map(function (objectId) {
        var hidden = (function () {
            var shown = Human.scene.objects[objectId].shown;
            switch (shown) {
                case undefined: return undefined;
                default: return !shown;
            }
        })();
        return {
            objectId: objectId,
            selected: SelectedObjectUtils.isObjectSelected(objectId, selectedNodes, viewGraph, graphService),
            hidden: hidden
        };
    });
}
function loadStory(store, storyId) {
    var type = storyId.includes('/')
        ? StoryType.Module
        : StoryType.Bookmark;
    store.dispatch(new LoadStory(storyId, type));
}
function captureScene(store, graphService, cameraService) {
    // wrangle state from the store or engine
    var camera = cameraService.captureCamera();
    var objects = captureObjects(store, graphService);
    var background = store
        .selectSnapshot(StoryChaptersState.getStoryChapters)
        .map(function (chapter) {
        return {
            chapterId: chapter.id,
            background: chapter.background || appDefaultBackground
        };
    });
    // no transformations necessary for this capture data
    var storyId = store.selectSnapshot(StoryState.getStoryId);
    var labels = store.selectSnapshot(StoryLabelsState.getLabels);
    var activeChapterId = store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
    var sceneCapture = {
        camera: camera,
        labels: labels,
        storyId: storyId,
        objects: objects,
        background: background,
        activeChapterId: activeChapterId
    };
    return sceneCapture;
}
function applyCapturedState(capture, store, engineService, isLegacyCapture) {
    if (isLegacyCapture === void 0) { isLegacyCapture = false; }
    return new Promise(function (resolve) {
        // handle selecting and hiding objects
        var viewGraph = store.selectSnapshot(EngineState.getViewGraph);
        var getNode = function (object) {
            return engineService.getNode(viewGraph, object.objectId);
        };
        var checkedNodes = capture.objects
            .filter(function (object) { return object.selected === true; })
            .map(getNode);
        // handle old capture format, where objects that are hidden are EXCLUDED from capture.objects
        // HACK the old format removed objects from the capture if they were hidden
        // so, if an object in the scene is missing from the capture, it should be toggled off.
        var hiddenNodes = (function () {
            if (isLegacyCapture) {
                var hiddenNodesLegacy = Human.scene.getLeaves()
                    .filter(function (objectId) {
                    var captureObject = capture.objects.find(function (object) { return object.objectId === objectId; });
                    var missingFromCapture = captureObject === undefined;
                    return missingFromCapture;
                })
                    .map(function (objectId) { return getNode({ objectId: objectId }); })
                    .filter(function (x) { return x; });
                return hiddenNodesLegacy;
            }
            else {
                return capture.objects
                    .filter(function (object) { return object.hidden === true; })
                    .map(getNode)
                    .filter(function (x) { return x; }); // some objects aren't in the view graph, so we have to filter them.
            }
        })();
        // handle background model for each chapter
        var setBackgroundActions = capture.background.map(function (backgroundCapture) {
            return new SetChapterBackground(backgroundCapture.chapterId, backgroundCapture.background);
        });
        var selectChapterAction = capture.activeChapterId
            ? [new SelectChapterById(capture.activeChapterId)]
            : [];
        var zoomSet = capture.camera.zoom !== undefined
            ? [new ZoomCameraStatic(capture.camera.zoom)]
            : [];
        // set Timeout, so that nodes are highlighted
        setTimeout(function () {
            store.dispatch(__spread([
                new SetCamera(capture.camera.lookAt, false),
                new SetLabels(capture.labels),
                new ToggleVisibleNodes(hiddenNodes, false),
                new SetCheckedNodes(checkedNodes)
            ], zoomSet, selectChapterAction, setBackgroundActions));
            resolve();
        });
    });
}
function restoreScene(capture, store, engineService, isLegacyCapture) {
    return new Observable(function (observer) {
        engineService
            .getEvent(EventsEnum.HumanStarted)
            .pipe(first())
            .subscribe(function () {
            applyCapturedState(capture, store, engineService, isLegacyCapture).then(function () {
                engineService.fireEvent(EventsEnum.SceneRestored, undefined);
                observer.next(true);
                observer.complete();
            });
        });
        loadStory(store, capture.storyId);
    });
}
function focusObject(params, store, paintService, engineService) {
    var _a;
    // enable xray mode if something is selected
    if (params.select && params.xray) {
        enableXrayMode(store, paintService, engineService);
    }
    else {
        disableXrayMode(store, engineService);
    }
    // set selected
    if (params.select) {
        setObjectSelected((_a = {}, _a[params.objectId] = true, _a), store, engineService);
    }
    // flyTo object
    var animate = true;
    var animationDuration = 1;
    store.dispatch(new FlyToObject(params.objectId, animate, animationDuration));
}
export function getSceneEvents() {
    return [
        {
            eventName: EventsEnum.ScenePicked,
            aliasEvents: [EventsEnum.PickPicked],
            mapFn: formatPickResult
        },
        {
            eventName: EventsEnum.SceneObjectsSelected,
            mapFn: function (event) { return event.selectedObjectsUpdate; }
        },
        {
            eventName: EventsEnum.SceneObjectsShown,
            mapFn: function (event) { return event.enabledObjectsUpdate; }
        },
        {
            eventName: EventsEnum.Debug_SceneReady,
            aliasEvents: [EventsEnum.Started]
        },
        {
            eventName: EventsEnum.SceneRestored,
            mapFn: function () { return true; }
        },
        {
            eventName: EventsEnum.SceneXrayEnabled,
            mapFn: function () { return true; }
        },
        {
            eventName: EventsEnum.SceneXrayDisabled,
            mapFn: function () { return true; }
        }
    ];
}
function setObjectSelected(params, store, engineService) {
    var viewGraph = store.selectSnapshot(EngineState.getViewGraph);
    var selectedNodes = Object.keys(params)
        .filter(function (objectId) { return viewGraph.nodes[objectId] !== undefined; }) // sanitize: filter objects that we don't know about.
        .filter(function (objectId) { return params[objectId] === true; })
        .map(function (objectId) { return engineService.getNode(viewGraph, objectId); });
    store.dispatch(new SetCheckedNodes(selectedNodes));
    return true;
}
function enableXrayMode(store, paintService, engineService) {
    var colorRecipes = engineService.getLeaves().map(function (leafId) {
        return {
            objectId: leafId,
            recipe: paintService.getColor(leafId)
        };
    });
    store.dispatch(new SetXrayModeCacheColorRecipes(colorRecipes));
    store.dispatch(new SetXrayMode(true));
    engineService.fireEvent(EventsEnum.SceneXrayEnabled, undefined);
    return true;
}
export function getSceneProcedures(store, engineService, paintService, cameraService, graphService, historyService) {
    return [
        {
            // legacy, keeping support for now
            procName: MessageEnum.focusObject,
            actionFn: function (params) {
                focusObject(params, store, paintService, engineService);
                return true;
            }
        },
        {
            procName: MessageEnum.dissectSetEnabled,
            actionFn: function (params) {
                var tool = params.enable ? ToolEnum.Hide : ToolEnum.Select;
                store.dispatch(new SetActiveTool(tool));
                return true;
            }
        },
        {
            procName: MessageEnum.sceneSetClickMode,
            actionFn: function (mode) {
                var tool = (function () {
                    if (mode === 'select') {
                        return ToolEnum.Select;
                    }
                    if (mode === 'hide') {
                        return ToolEnum.Hide;
                    }
                    if (mode === 'fade') {
                        return ToolEnum.Fade;
                    }
                    return ToolEnum.Select;
                })();
                store.dispatch(new SetActiveTool(tool));
                return true;
            }
        },
        {
            procName: MessageEnum.pickSingleSetEnabled,
            actionFn: function (params) {
                var enable = (function () {
                    switch (params.enable) {
                        case undefined: return !store.selectSnapshot(ViewState.getMultiPickModeEnabled);
                        case true: return false;
                        case false: return true;
                    }
                })();
                store.dispatch(new ViewActions.SetMultiPickModeEnabled(enable));
                return true;
            }
        },
        {
            procName: MessageEnum.pickMultiSetEnabled,
            actionFn: function (params) {
                var enable = (function () {
                    switch (params.enable) {
                        case undefined: return !store.selectSnapshot(ViewState.getMultiPickModeEnabled);
                        default: return params.enable;
                    }
                })();
                store.dispatch(new ViewActions.SetMultiPickModeEnabled(enable));
                return true;
            }
        },
        {
            procName: MessageEnum.getCanvasPos,
            actionFn: function (params) {
                var canvas = engineService.getCanvas();
                return _3DUtils.getCanvasPos(Human, params.worldPos, params.offset, canvas.width, canvas.height);
            }
        },
        {
            procName: MessageEnum.SceneCapture,
            actionFn: function () { return captureScene(store, graphService, cameraService); }
        },
        {
            procName: MessageEnum.SceneRestore,
            actionFn: function (capture) {
                var isLegacyCapture = SceneAPIUtils.isLegacyCaptureFormat(capture);
                var sanitizedCapture = isLegacyCapture
                    ? SceneAPIUtils.convertCaptureFormat(capture)
                    : capture;
                return restoreScene(sanitizedCapture, store, engineService, isLegacyCapture);
            }
        },
        {
            procName: MessageEnum.SceneEnableXrayMode,
            actionFn: function () { return enableXrayMode(store, paintService, engineService); }
        },
        {
            procName: MessageEnum.SceneDisableXrayMode,
            actionFn: function () { return disableXrayMode(store, engineService); }
        },
        {
            procName: MessageEnum.SceneLoad,
            actionFn: function (id) {
                loadStory(store, id);
                return true;
            }
        },
        {
            procName: MessageEnum.SceneReset,
            actionFn: function () {
                historyService.resetChapter();
                return true;
            }
        },
        {
            procName: MessageEnum.SceneInfo,
            actionFn: function (params) {
                var objectIds = params && params.objectId
                    ? [params.objectId]
                    : Object.keys(Human.scene.objects);
                var displayName = store.selectSnapshot(StoryState.getStory).displayName;
                return buildSceneInfoResponse(displayName, objectIds, store, graphService);
            }
        },
        {
            procName: MessageEnum.ScenePick,
            actionFn: function (params) {
                var pick = {
                    x: params.x,
                    y: params.y,
                    rayPick: true
                };
                var hit = engineService.pickScene(pick);
                return formatPickResult(hit);
            }
        },
        {
            procName: MessageEnum.SceneSelectObjects,
            actionFn: function (params) { return setObjectSelected(params, store, engineService); }
        },
        {
            procName: MessageEnum.SceneShowObjects,
            actionFn: function (params) {
                engineService.setNodesVisible(params);
                return true;
            }
        },
        {
            // only included to support migration away from scene.selectionMode
            procName: MessageEnum.IsolateObject,
            actionFn: function (objectId) {
                // hide all objects
                // show object in params
                var e_1, _a;
                var objects = {};
                try {
                    for (var _b = __values(Human.scene.getLeaves()), _c = _b.next(); !_c.done; _c = _b.next()) {
                        var leafId = _c.value;
                        objects[leafId] = false; // do not show
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
                objects[objectId] = true;
                engineService.setNodesVisible(objects);
                return true;
            }
        },
        {
            procName: MessageEnum.SceneTransformObject,
            actionFn: function (params) {
                engineService.transformObject(params.objectId, params);
                return true;
            }
        },
        {
            procName: MessageEnum.SceneColorObject,
            actionFn: function (params) {
                var colorRecipe = new ColorRecipeModel(params);
                paintService.colorObject(params.objectId, colorRecipe);
                return true;
            }
        },
        {
            procName: MessageEnum.SceneGetColor,
            actionFn: function (params) {
                return paintService.getColor(params.objectId);
            }
        },
        {
            procName: MessageEnum.SceneUncolorObject,
            actionFn: function (params) {
                paintService.uncolorObject(params.objectId);
                return true;
            }
        },
        {
            procName: MessageEnum.SceneDisableHighlight,
            actionFn: function () {
                paintService.disableHighlight();
                return true;
            }
        },
        {
            procName: MessageEnum.SceneEnableHighlight,
            actionFn: function () {
                paintService.enableHighlight();
                return true;
            }
        }
    ];
}
